import { Formik, Form, FormikErrors, FormikTouched } from 'formik';
import * as Yup from 'yup';
import Input from '../../../../components/form-control/Input';
import Button from '../../../../components/form-control/Button';
import { IMultiSelectItem, MultiSelect } from '../../../../components/form-control/MultiSelect';
import InputSelectMenu from '../../../../components/form-control/InputSelectMenu';
import SelectDropdown, {
  ISelectDropdownItem
} from '../../../../components/form-control/SelectDropdown';
import SelectMenu, {
  ISelectMenuItemValue,
  MiniSelectMenu
} from '../../../../components/form-control/SelectMenu';
import VerifyInput from '../../../../components/VerifyInput';
import { REGIONS_BY_COUNTRY, VEHICLE_COLORS } from '../../../../resources/constants';
import {
  calculateNumberOfDaysBetweenTwoPeriods,
  capitalizeSentence,
  getDriverLicenseProviderByCountry,
  getFormattedPhoneNumber,
  getNatureOfInterestListByCountry,
  getPhoneNumberFromShortCodeFormat,
  getSelectableManufacturingYears,
  registrationNumberPlaceholder,
  sentenceCase,
  showToast
} from '../../../../utils/miscFunctions';
import {
  endorsePolicy,
  getAllRiskTypes,
  searchVehicleMake,
  searchVehicleModelsByMake
} from '../../../../api/requests';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getEnumsFromState, getSelectedCountry } from '../../../../states/slices/resourceSlice';
import { Policy } from '../../../../models/policy/Policy';
import { Car, ClipboardList } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { InputRadio } from '../../../../components/form/InputRadio';
import moment from 'moment';
import DatePickerInput from '../../../../components/form-control/DatePickerInput';
import useIntermediaries from '../../../../hooks/intermediariesHook';
import { IEnum } from '../../../../resources/interfaces';
import { IVehicle } from '../../../../models/vehicle/IVehicle';
import { Vehicle } from '../../../../models/vehicle/Vehicle';

const currentYear = new Date().getFullYear();

interface VehicleInformationFormProps {
  onSubmit: (values: VehicleFormValues) => void;
  policy?: Policy;
  onClose: () => void;
  isVehicleEdit?: boolean;
  isVehicleOwnerEdit?: boolean;
  isOtherInfoEdit?: boolean;
  isInsuranceInfoEdit?: boolean;
  isRefereeInfoEdit?: boolean;
  selectedVehicleNumber?: string;
}

export interface VehicleFormValues {
  make: string;
  model: string;
  bodyType: string | undefined;
  color: string;
  chassisNumber: string;
  cc: number | null;
  manufacturedYear: number | null;
  registrationYear: number | null;
  registrationNumber: string;
  maxSeat: number | null;
  vehicleRegion: string;
  powerType?: string;
  vehicleAltered?: string;
  vehicleInGoodRepairState?: string;
  driverHasComplications?: string;
  hasDeclinedClaims?: string;
  hasPreviousClaims?: string;
  hasInterestedParties?: string;
  alterationDetails?: string;
  repairState?: string;
  driverComplications?: string;
  declinedInsuranceDetails?: string;

  //Insurance Info
  insurerId: string;
  startsOn: Date | null;
  expiresOn: Date | null;
  insurerPolicyNumber?: string | null;
  previousInsuranceClaims?: string;

  //Owner Info
  composerIsOwner: 'yes' | 'no' | '';
  natureOfInterestInVehicle?: string;
  ownerName: string;
  ownerAddress?: string;
  ownerPhone: string;
  interestedParties?: string;
  vehicleMainDriver?: string;
  vehicleInsuredValue?: number;
  installedAccessoriesValue?: number;

  //Referee Info
  name?: string;
  phone?: string;
  relationship?: string;
  yearsKnown?: string;
}

export function VehicleInformationForm({
  onSubmit,
  policy,
  onClose,
  isVehicleEdit = false,
  isVehicleOwnerEdit = false,
  isOtherInfoEdit = false,
  isInsuranceInfoEdit = false,
  isRefereeInfoEdit = false,
  selectedVehicleNumber
}: VehicleInformationFormProps) {
  const { intermediaries } = useIntermediaries();
  const [riskTypes, setRiskTypes] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const enums = useSelector(getEnumsFromState);
  const queryClient = useQueryClient();
  const selectedVehicle: Vehicle | null = policy?.vehicles && selectedVehicleNumber ?
    policy?.vehicles?.filter((vehicle: Vehicle) => String(vehicle?.registrationNumber) === selectedVehicleNumber)[0] :
    null;

  const defaultValues: VehicleFormValues = {
    make: selectedVehicle?.make || '',
    model: selectedVehicle?.model || '',
    bodyType: selectedVehicle?.type?.name.replace(/[\s/]+/g, '_').toUpperCase() || '',
    color: selectedVehicle?.color || '',
    chassisNumber: selectedVehicle?.chassisNumber || '',
    cc: selectedVehicle?.cc || null,
    manufacturedYear: selectedVehicle?.manufacturedYear || null,
    registrationYear: selectedVehicle?.registrationYear || null,
    registrationNumber: selectedVehicle?.registrationNumber || '',
    maxSeat: selectedVehicle?.maxSeat || null,
    vehicleRegion: selectedVehicle?.region || '',
    // A saved co-owner (vehicle.ownerName) means the applicant isn't the sole registered
    // owner — default to 'no' so the co-owner section opens and its details prefill.
    composerIsOwner: selectedVehicle?.ownerName ? 'no' : 'yes',
    natureOfInterestInVehicle: selectedVehicle?.natureOfInterest || '',
    ownerName: selectedVehicle?.ownerName || '',
    ownerAddress: selectedVehicle?.ownerAddress || '',
    ownerPhone: (selectedVehicle?.ownerPhoneNumber && getPhoneNumberFromShortCodeFormat(selectedVehicle?.ownerPhoneNumber)) || '',
    vehicleMainDriver: selectedVehicle?.driver?.fullName || '',
    vehicleInsuredValue: policy?.quote?.insuredValue || undefined,
    installedAccessoriesValue: selectedVehicle?.installedAccessoriesValue || undefined,
    vehicleAltered: selectedVehicle?.vehicleAltered ? 'yes' : 'no',
    vehicleInGoodRepairState: selectedVehicle?.goodRepairState ? 'yes' : 'no',
    driverHasComplications: selectedVehicle?.driver?.hasDisease ? 'yes' : 'no',
    hasDeclinedClaims: policy?.previouslyDeclined ? 'yes' : 'no',
    hasPreviousClaims: policy?.previouslyIssuedClaim ? 'yes' : 'no',
    previousInsuranceClaims: policy?.previousInsuranceClaims || '',
    hasInterestedParties: selectedVehicle?.interestedParties ? 'yes' : 'no',
    insurerId: policy?.insurer?.id || '',
    startsOn: policy?.startsOn ? policy?.startsOn : null,
    expiresOn: policy?.expiresOn ? policy?.expiresOn : null,
    alterationDetails: selectedVehicle?.alterationDetails || '',
    repairState: selectedVehicle?.repairState || '',
    driverComplications: selectedVehicle?.driver?.driverComplications || '',
    declinedInsuranceDetails: policy?.declinedInsuranceDetails || '',
    name: policy?.referee?.name || '',
    phone: policy?.referee?.phone || '',
    relationship: policy?.referee?.relationship || '',
    yearsKnown: policy?.referee?.yearsKnown || '',
    insurerPolicyNumber: policy?.insurerPolicyNumber || null,
  };

  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1;
  const countryIso = selectedCountry.iso ?? 'GH';
  const [vehicleMakes, setVehicleMakes] = useState<string[]>([]);
  const [loadingMakes, setLoadingMakes] = useState<boolean>(false);
  const [vehicleMakePrefix, setVehicleMakePrefix] = useState<string>('');
  const [selectedVehicleMake, setSelectedVehicleMake] = useState<string | null>(null);
  const [vehicleModels, setVehicleModels] = useState<any>([]);
  const MANUFACTURED_YEARS_LIST = getSelectableManufacturingYears();
  const [validManufacturingYears, setValidManufacturingYears] = useState(
    getSelectableManufacturingYears()
  );

  const endorseApprovedPolicyInfo = async (policyNumber: string, values: VehicleFormValues) => {
    if (!values) return null;
    setIsLoading(true);
    const payload: { vehicle: any; insurance: any; referee?: any } = {
      vehicle: {
        id: selectedVehicle?.id,
        maker: values.make,
        model: values.model,
        vehicleType: values.bodyType || null,
        color: values.color,
        chassisNumber: values.chassisNumber,
        cc: values.cc,
        yearIssued: values.manufacturedYear,
        registrationYear: values.registrationYear,
        registrationNumber: values.registrationNumber,
        maxSeat: values.maxSeat,
        vehicleRegion: values.vehicleRegion ? values.vehicleRegion : null,
        composerIsOwner:
          values.composerIsOwner === 'yes' ? true : values.composerIsOwner === 'no' ? false : false,
        natureOfInterest: values.natureOfInterestInVehicle
          ? values.natureOfInterestInVehicle
          : null,
        ownerName: values.ownerName ? values.ownerName : null,
        ownerAddress: values.ownerAddress ? values.ownerAddress : null,
        ownerPhone: values.ownerPhone ? getFormattedPhoneNumber(values.ownerPhone) : null,
        vehicleMainDriver: values.vehicleMainDriver ? values.vehicleMainDriver : null,
        issuedValue: values.vehicleInsuredValue,
        installedAccessoriesValue: values.installedAccessoriesValue,
        vehicleAltered: values.vehicleAltered === 'yes' ? true : false,
        vehicleInGoodRepairState: values.vehicleInGoodRepairState === 'yes' ? true : false,
        driverHasComplications: values.driverHasComplications === 'yes' ? true : false,
        driverComplications: values.driverComplications ? values.driverComplications : null,
        alterationDetails: values.alterationDetails ? values.alterationDetails : null,
        repairState: values.repairState ? values.repairState : null
      },
      insurance: {
        hasDeclinedClaims: values.hasDeclinedClaims === 'yes' ? true : false,
        declinedInsuranceDetails: values.declinedInsuranceDetails
          ? values.declinedInsuranceDetails
          : null,
        hasPreviousClaims: values.hasPreviousClaims === 'yes' ? true : false,
        previousInsuranceClaims: values.previousInsuranceClaims
          ? values.previousInsuranceClaims
          : null,
        hasInterestedParties: values.hasInterestedParties === 'yes' ? true : false,
        insurerId: values.insurerId,
        startsOn: moment(values.startsOn).format('YYYY-MM-DD'),
        expiresOn: values.expiresOn
          ? moment(values.expiresOn).format('YYYY-MM-DD')
          : null,
        insurerPolicyNumber: values.insurerPolicyNumber,
      }
    };

    if (values.name || values.phone) {
      payload.referee = {
        name: values.name || null,
        phone: values.phone ? getFormattedPhoneNumber(values.phone) : null,
        relationship: values.relationship || null,
        yearKnown: values.yearsKnown || null
      };
    }

    await endorsePolicy(policyNumber, payload)
      .then((response: any) => {
        onClose();
        setIsLoading(false);
        showToast.success(response?.data?.message);
        queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
      })
      .catch((error: any) => {
        setIsLoading(false);
        showToast.error(error.description);
      })
      .finally(() => setIsLoading(false));
  };

  const seachForVehicleMake = async () => {
    try {
      let makes = await searchVehicleMake(vehicleMakePrefix?.toUpperCase());
      if (makes) {
        setVehicleMakes(makes?.data?.splice(0, 100));
        setTimeout(() => {
          setLoadingMakes(false);
        }, 3000);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const seachForVehicleModel = async () => {
    try {
      if (selectedVehicleMake) {
        let models = await searchVehicleModelsByMake(selectedVehicleMake?.toUpperCase()?.trim());
        if (models?.data) {
          setVehicleModels(models?.data);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  useQuery({ queryKey: ['getRiskTypes'], queryFn: () => getRiskTypes(), enabled: isVehicleEdit });
  const getRiskTypes = async () => {
    await getAllRiskTypes()
      .then((response: any) => {
        setRiskTypes(response);
      })
      .catch((error: Error) => {
        console.log('error', error);
      })
      .finally(() => {
        // setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedVehicleMake && isVehicleEdit) {
      setVehicleModels([]);
      seachForVehicleModel();
    }
  }, [selectedVehicleMake]);

  useEffect(() => {
    if (isVehicleEdit) {
      setLoadingMakes(true);
      seachForVehicleMake();
    }
  }, [vehicleMakePrefix]);

  const ownerValidationSchema = Yup.object().shape({
    vehicleInsuredValue: Yup.number().required('Insured value is required').nullable(),
    ownerName: Yup.string().required("Owner's name is required").nullable(),
    ownerPhone: Yup.number().required("Owner's name is required").nullable(),
    composerIsOwner: Yup.string()
      .oneOf(['yes', 'no'], 'Please select an option')
      .required('This field is required'),
    natureOfInterestInVehicle: Yup.string().when('composerIsOwner', {
      is: 'no',
      then: Yup.string().required('Nature of interest is required'),
      otherwise: Yup.string().notRequired()
    })
  });

  const insuranceValidationSchema = Yup.object().shape({
    insurerId: Yup.string().required('Insurer name is required'),
    startsOn: Yup.date()
      .required('Policy start date is required')
      .max(Yup.ref('expiresOn'), 'Start date must be before end date'),
  });

  const refereeValidationSchema = Yup.object().shape({
    name: Yup.string().required('Referee name is required'),
    phone: Yup.number().required('Referee name is required').nullable()
  });

  const vehicleValidationSchema = Yup.object().shape({
    make: Yup.string().required('Make is required').nullable(),
    model: Yup.string().required('Model is required').nullable(),
    bodyType: Yup.string().required('Vehicle type is required').nullable(),
    color: Yup.string().required('Color is required').nullable(),
    chassisNumber: Yup.string()
      .required('Chassis/VIN is required')
      .min(17, 'VIN must be 17 characters')
      .max(17, 'VIN must be 17 characters')
      .nullable(),
    cc: Yup.number()
      .required('CC is required')
      .positive('CC must be positive')
      .integer('CC must be a whole number')
      .nullable(),
    manufacturedYear: Yup.number()
      .required('Manufactured year is required')
      .min(1900, 'Invalid year')
      .max(currentYear, 'Year cannot be in the future')
      .nullable(),
    registrationYear: Yup.number()
      .required('Registration year is required')
      .min(1900, 'Invalid year')
      .max(currentYear, 'Year cannot be in the future')
      .nullable(),
    registrationNumber: Yup.string().required('Registration number is required').nullable(),
    maxSeat: Yup.number()
      .required('Number of passengers is required')
      .min(1, 'At least 1 passenger')
      .integer('Must be a whole number')
      .nullable(),
    vehicleRegion: Yup.string().required('Vehicle region is required').nullable()
  });

  const getVehicleInfoEditForm = (
    values: VehicleFormValues,
    handleBlur: any,
    handleChange: any,
    touched: FormikTouched<VehicleFormValues>,
    errors: FormikErrors<VehicleFormValues>,
    setFieldValue: any
  ) => {
    return (
      isVehicleEdit && (
        <>
          <div className="p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-700 dark:text-gray-200">
              <Car className="h-5 w-5 text-primary-main" />
              Vehicle Specifications
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MultiSelect
                label="Vehicle Make"
                selected={[values.make]}
                onChange={(val: string[]) => {
                  setFieldValue('make', val?.[0]);
                  if (val && val.length > 0) {
                    setSelectedVehicleMake(val[0]);
                  }
                }}
                list={vehicleMakes?.map(
                  (make: any) =>
                  ({
                    name: make,
                    value: make
                  } as IMultiSelectItem)
                )}
                placeholder="Search and select your vehicle make"
                selectInputClassName="!shadow-none"
                error={touched.make ? errors.make : undefined}
                helperText={touched.make ? errors.make : undefined}
                required
                onQueryChange={(query: string) => {
                  setVehicleMakePrefix(query);
                }}
                multiSelect={false}
                loading={loadingMakes}
              />

              <InputSelectMenu
                name={'model'}
                label="Vehicle Model"
                placeholder="Select your vehicle model"
                // description="vehicle model - type"
                list={vehicleModels}
                listSelectedValue={values.model}
                onChange={(val: string) => {
                  setFieldValue('model', val);
                }}
                error={touched.model ? errors.model : undefined}
                helperText={touched.model ? errors.model : undefined}
                required
                search
                searchPlaceHolder="Enter your vehicle model. Eg. Yaris"
                sort
              />

              <SelectDropdown
                label="Vehicle Type"
                name="bodyType"
                listSelectedValue={values.bodyType}
                onChange={(val: ISelectDropdownItem | null) => {
                  setFieldValue('bodyType', val);
                }}
                onBlur={handleBlur}
                search
                searchPlaceHolder="Search by vehicle type ..."
                list={enums?.vehicleBodyTypes?.map((type: IEnum) => ({
                  name: (capitalizeSentence(type.title)),
                  value: type.key
                }))} placeholder="Select vehicle type"
                error={touched.bodyType ? errors.bodyType : undefined}
                helperText={touched?.bodyType ? errors.bodyType : undefined}
                required
              />

              <InputSelectMenu
                label="Vehicle Colour"
                name="color"
                listSelectedValue={values.color}
                onChange={(val: ISelectMenuItemValue | null) => {
                  setFieldValue('color', val);
                }}
                onBlur={handleBlur}
                search
                searchPlaceHolder="Search by vehicle colour ..."
                list={VEHICLE_COLORS}
                placeholder="Select vehicle colour"
                error={touched.color ? errors.color : undefined}
                helperText={touched?.color ? errors.color : undefined}
                required
              />

              <VerifyInput
                name="chassisNumber"
                label="Chassis Number / VIN"
                verified={false}
                value={values?.chassisNumber}
                onClick={() => { }}
                onChange={handleChange}
                error={
                  errors.chassisNumber && touched.chassisNumber ? errors.chassisNumber : undefined
                }
                helperText={
                  errors.chassisNumber && touched.chassisNumber ? errors.chassisNumber : undefined
                }
                required={true}
                onBlur={handleBlur}
              />

              <Input
                label={values.powerType === 'hp' ? 'Horse Power' : 'Cubic Capacity'}
                name="cc"
                value={values.cc}
                append={
                  <MiniSelectMenu
                    name="powerType"
                    options={['cc', 'hp']}
                    value={values.powerType}
                    onChange={(e: any) => {
                      const val = e?.target?.value;
                      if (val) {
                        setFieldValue('powerType', val);
                        if (val == 'cc') {
                          setFieldValue('cc', values?.cc && Math.round(values?.cc * 15));
                        } else {
                          setFieldValue(
                            'cc',
                            values?.cc && Math.round((values?.cc / 15) * 100) / 100
                          );
                        }
                      }
                    }}
                  />
                }
                type="number"
                min={50}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Cubic capacity"
                error={touched.cc ? errors.cc : undefined}
                helperText={touched.cc ? errors.cc : undefined}
                required
              />
            </div>
          </div>
          {/* Registration & Usage Section */}
          <div className="p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-700 dark:text-gray-200">
              <ClipboardList className="h-5 w-5 text-primary-main" />
              Registration & Usage
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputSelectMenu
                label="Manufactured Year"
                name="manufacturedYear"
                listSelectedValue={values.manufacturedYear}
                onChange={(val: string | null) => {
                  setFieldValue('manufacturedYear', val);
                }}
                onBlur={handleBlur}
                list={validManufacturingYears}
                placeholder="Select manufactured year"
                required
                sort
                emptyListMessage="Select manufactured year to proceed"
                error={touched.manufacturedYear ? errors.manufacturedYear : undefined}
                helperText={touched.manufacturedYear ? errors.manufacturedYear : undefined}
              />

              <InputSelectMenu
                label="Registration Year"
                name="registrationYear"
                listSelectedValue={values.registrationYear}
                onChange={(val: string | null) => {
                  setFieldValue('registrationYear', val);
                }}
                onBlur={handleBlur}
                list={MANUFACTURED_YEARS_LIST}
                placeholder="Select registration year"
                error={touched.registrationYear ? errors.registrationYear : undefined}
                helperText={touched.registrationYear ? errors.registrationYear : undefined}
                emptyListMessage="Select registration year"
                required
                sort
              />

              <Input
                description={''}
                type="text"
                id="registrationNumber"
                label="Registration Number"
                placeholder={registrationNumberPlaceholder(selectedCountry.iso, policy)}
                className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                wrapperClasses="pr-0"
                value={values?.registrationNumber?.toUpperCase()}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched?.registrationNumber ? errors.registrationNumber : undefined}
                helperText={touched?.registrationNumber ? errors.registrationNumber : undefined}
                required
              />

              <Input
                type="number"
                id="maxSeat"
                label="Number of Passengers"
                className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                value={values?.maxSeat ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Vehicle max seat"
                required
                error={touched?.maxSeat ? errors.maxSeat : undefined}
                helperText={touched?.maxSeat ? errors.maxSeat : undefined}
              />

              <SelectMenu
                label="Region"
                name="vehicleRegion"
                listSelectedValue={
                  values?.vehicleRegion ? sentenceCase(values?.vehicleRegion) : undefined
                }
                onChange={(val: ISelectMenuItemValue | null) => {
                  setFieldValue('vehicleRegion', val);
                }}
                onBlur={handleBlur}
                list={REGIONS_BY_COUNTRY[selectedCountry.iso]}
                placeholder="Select region"
                error={touched.vehicleRegion ? errors.vehicleRegion : undefined}
                helperText={touched?.vehicleRegion ? errors.vehicleRegion : undefined}
                required
              />
            </div>
          </div>
        </>
      )
    );
  };

  const getInsuranceInfoEditForm = (
    values: VehicleFormValues,
    handleChange: any,
    touched: FormikTouched<VehicleFormValues>,
    errors: FormikErrors<VehicleFormValues>,
    setFieldValue: any
  ) => {
    return (
      isInsuranceInfoEdit && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 rounded-lg">
            {/* Insurer Name */}
            <SelectDropdown
              label="Insurer"
              name="insurerId"
              listSelectedValue={values.insurerId}
              onChange={(val: ISelectDropdownItem | null) => {
                setFieldValue('insurerId', val);
              }}
              //onBlur={handleBlur}
              search
              searchPlaceHolder="Search by insurer ..."
              list={intermediaries?.map(
                (insurer: any) =>
                ({
                  name: insurer.insurerName,
                  value: insurer.insurerId
                } as ISelectDropdownItem)
              )}
              placeholder="Select insurer"
              error={touched.insurerId ? errors.insurerId : undefined}
              helperText={touched?.insurerId ? errors.insurerId : undefined}
              required
            />

            {/* Policy Start Date */}
            <DatePickerInput
              id="startsOn"
              label="Policy Start Date"
              selected={values?.startsOn ? new Date(values?.startsOn) : null}
              onChange={(value) => {
                setFieldValue('startsOn', value);
                const expiresOn = moment(value).add(1, 'year').subtract(1, 'day');
                expiresOn && setFieldValue('expiresOn', expiresOn);
              }}
              required
              error={touched.startsOn ? errors.startsOn : undefined}
              helperText={touched?.startsOn ? errors.startsOn : undefined}
            />

            <DatePickerInput
              id="expiresOn"
              label="Policy End Date"
              selected={values?.expiresOn ? new Date(values?.expiresOn) : null}
              onChange={(value: Date | null) => {
                setFieldValue('expiresOn', value);
              }}
              required
              disabled
            />

            <Input
              type="text"
              id=""
              label='Total Days'
              value={values.startsOn && values.expiresOn ? calculateNumberOfDaysBetweenTwoPeriods(values.startsOn, values.expiresOn) : ""}
              className='dark:text-darkBg-mainText dark:bg-gray-600'
              readOnly
            />

            <Input
              type="text"
              id="insurerPolicyNumber"
              name='insurerPolicyNumber'
              label='Insurer Reference'
              onChange={handleChange}
              value={values.insurerPolicyNumber}
              className='dark:text-darkBg-mainText'
            />
          </div>
        </div>
      )
    );
  };

  const getOtherInfoEditForm = (
    values: VehicleFormValues,
    handleChange: any,
    touched: FormikTouched<VehicleFormValues>,
    errors: FormikErrors<VehicleFormValues>
  ) => {
    return (
      isOtherInfoEdit && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 rounded-lg">
          <div className="flex flex-col gap-2 justify-center">
            <InputRadio
              name="vehicleAltered"
              label={'Has vehicle been altered in any way?'}
              options={[
                { name: 'Yes', value: 'yes' },
                { name: 'No', value: 'no' }
              ]}
              selected={
                values.vehicleAltered !== undefined
                  ? values.vehicleAltered === 'yes'
                    ? 'yes'
                    : 'no'
                  : undefined
              }
              onSelect={handleChange}
              error={touched?.vehicleAltered ? errors.vehicleAltered : undefined}
              helperText={touched?.vehicleAltered ? errors.vehicleAltered : undefined}
            />

            {values.vehicleAltered === 'yes' && (
              <Input
                type="text"
                name="alterationDetails"
                label="Vehicle Alteration Details"
                className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                value={values?.alterationDetails}
                onChange={handleChange}
              //onBlur={handleBlur}
              // required
              // error={touched.alterationDetails ? errors.alterationDetails : undefined}
              // helperText={touched?.alterationDetails ? errors.alterationDetails : undefined}
              />
            )}
          </div>
          <div className="flex flex-col gap-2 justify-center">
            <InputRadio
              name="vehicleInGoodRepairState"
              label={'Is this vehicle in good repair state?'}
              options={[
                { name: 'Yes', value: 'yes' },
                { name: 'No', value: 'no' }
              ]}
              selected={
                values.vehicleInGoodRepairState !== undefined
                  ? values.vehicleInGoodRepairState === 'yes'
                    ? 'yes'
                    : 'no'
                  : undefined
              }
              onSelect={handleChange}
              error={
                touched?.vehicleInGoodRepairState ? errors.vehicleInGoodRepairState : undefined
              }
              helperText={
                touched?.vehicleInGoodRepairState ? errors.vehicleInGoodRepairState : undefined
              }
            />
            {values.vehicleInGoodRepairState === 'yes' && (
              <Input
                type="text"
                name="repairState"
                label="Vehicle repair state details"
                className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                value={values?.repairState}
                onChange={handleChange}
              //onBlur={handleBlur}
              // required
              // error={touched.repairState ? errors.repairState : undefined}
              // helperText={touched?.repairState ? errors.repairState : undefined}
              />
            )}
          </div>
          <div className="flex flex-col gap-2 justify-center">
            <InputRadio
              name="driverHasComplications"
              label={'Does driver have any disease or complications'}
              options={[
                { name: 'Yes', value: 'yes' },
                { name: 'No', value: 'no' }
              ]}
              selected={
                values.driverHasComplications !== undefined
                  ? values.driverHasComplications === 'yes'
                    ? 'yes'
                    : 'no'
                  : undefined
              }
              onSelect={handleChange}
              error={touched?.driverHasComplications ? errors.driverHasComplications : undefined}
              helperText={
                touched?.driverHasComplications ? errors.driverHasComplications : undefined
              }
            />
            {values.driverHasComplications === 'yes' && (
              <Input
                type="text"
                name="driverComplications"
                label="Driver disease or complications"
                className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                value={values?.driverComplications}
                onChange={handleChange}
              //onBlur={handleBlur}
              // required
              // error={touched.driverComplications ? errors.driverComplications : undefined}
              // helperText={touched?.driverComplications ? errors.driverComplications : undefined}
              />
            )}
          </div>
          <div className="flex flex-col gap-2 justify-center">
            <InputRadio
              name="hasDeclinedClaims"
              label={'Ever been declined by other insurer?'}
              options={[
                { name: 'Yes', value: 'yes' },
                { name: 'No', value: 'no' }
              ]}
              selected={
                values.hasDeclinedClaims !== undefined
                  ? values.hasDeclinedClaims === 'yes'
                    ? 'yes'
                    : 'no'
                  : undefined
              }
              onSelect={handleChange}
              error={touched?.hasDeclinedClaims ? errors.hasDeclinedClaims : undefined}
              helperText={touched?.hasDeclinedClaims ? errors.hasDeclinedClaims : undefined}
            />
            {values.hasDeclinedClaims === 'yes' && (
              <Input
                type="text"
                name="declinedInsuranceDetails"
                label="Declined details"
                className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                value={values?.declinedInsuranceDetails}
                onChange={handleChange}
              //onBlur={handleBlur}
              // required
              // error={touched.declinedInsuranceDetails ? errors.declinedInsuranceDetails : undefined}
              // helperText={touched?.declinedInsuranceDetails ? errors.declinedInsuranceDetails : undefined}
              />
            )}
          </div>
          <div className="flex flex-col gap-2 justify-center">
            <InputRadio
              name="hasPreviousClaims"
              label={'Have you ever made a claim on another insurance policy?'}
              options={[
                { name: 'Yes', value: 'yes' },
                { name: 'No', value: 'no' }
              ]}
              selected={
                values.hasPreviousClaims !== undefined
                  ? values.hasPreviousClaims === 'yes'
                    ? 'yes'
                    : 'no'
                  : undefined
              }
              onSelect={handleChange}
              error={touched?.hasPreviousClaims ? errors.hasPreviousClaims : undefined}
              helperText={touched?.hasPreviousClaims ? errors.hasPreviousClaims : undefined}
            />
            {values.hasPreviousClaims === 'yes' && (
              <Input
                type="text"
                name="previousInsuranceClaims"
                label="Previous claim details"
                className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                value={values?.previousInsuranceClaims}
                onChange={handleChange}
              //onBlur={handleBlur}
              // required
              // error={touched.previousInsuranceClaims ? errors.previousInsuranceClaims : undefined}
              // helperText={touched?.previousInsuranceClaims ? errors.previousInsuranceClaims : undefined}
              />
            )}
          </div>
          <InputRadio
            name="hasInterestedParties"
            label={'Is this vehicle on hire purchase?'}
            options={[
              { name: 'Yes', value: 'yes' },
              { name: 'No', value: 'no' }
            ]}
            selected={
              values.hasInterestedParties !== undefined
                ? values.hasInterestedParties === 'yes'
                  ? 'yes'
                  : 'no'
                : undefined
            }
            onSelect={handleChange}
            error={touched?.hasInterestedParties ? errors.hasInterestedParties : undefined}
            helperText={touched?.hasInterestedParties ? errors.hasInterestedParties : undefined}
          />
        </div>
      )
    );
  };

  const getOwnerInfoEditForm = (
    values: VehicleFormValues,
    handleBlur: any,
    handleChange: any,
    touched: FormikTouched<VehicleFormValues>,
    errors: FormikErrors<VehicleFormValues>,
    setFieldValue: any
  ) => {
    return (
      isVehicleOwnerEdit && (
        <div className="space-y-8 p-6 rounded-lg">
          {
            <>
              <div className={`space-y-8 md:space-y-0 md:grid md:grid-cols-2 md:gap-4`}>
                <Input
                  type="text"
                  id="ownerName"
                  label="Vehicle Owner's Name"
                  placeholder="Eg. Kwaku Manu"
                  className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                  value={values?.ownerName}
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.ownerName ? errors.ownerName : undefined}
                  helperText={touched?.ownerName ? errors.ownerName : undefined}
                />

                <Input
                  type="tel"
                  id="ownerPhone"
                  label="Owner's Phone"
                  placeholder="Eg. Kwaku Manu"
                  className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                  value={values.ownerPhone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched?.ownerPhone ? errors.ownerPhone : undefined}
                  helperText={touched?.ownerPhone ? errors.ownerPhone : undefined}
                />

                <Input
                  type="text"
                  id="vehicleMainDriver"
                  label="Vehicle Main Driver"
                  placeholder="Eg. Francis Atuahene"
                  className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                  value={values?.vehicleMainDriver ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <Input
                  type="number"
                  id="vehicleInsuredValue"
                  label={`Vehicle's Insured Value (Sum Insured in ${selectedCountry.currencySymbol})`}
                  className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                  value={values?.vehicleInsuredValue}
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.vehicleInsuredValue ? errors.vehicleInsuredValue : undefined}
                  helperText={touched?.vehicleInsuredValue ? errors.vehicleInsuredValue : undefined}
                />

                <Input
                  type="number"
                  id="installedAccessoriesValue"
                  label="Acessories value"
                  placeholder=""
                  className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                  value={values.installedAccessoriesValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched?.installedAccessoriesValue
                      ? errors.installedAccessoriesValue
                      : undefined
                  }
                  helperText={
                    touched?.installedAccessoriesValue
                      ? errors.installedAccessoriesValue
                      : undefined
                  }
                />
              </div>
            </>
          }
          <div>
            <SelectDropdown
              name="composerIsOwner"
              label={`Are you the registered owner of the vehicle in the ${getDriverLicenseProviderByCountry(
                countryIso
              )} records?`}
              list={[
                {
                  name: 'Yes',
                  value: 'yes'
                },
                {
                  name: 'No, Another person',
                  value: 'no'
                }
              ]}
              listSelectedValue={
                values.composerIsOwner !== undefined
                  ? values.composerIsOwner === 'yes'
                    ? 'yes'
                    : 'no'
                  : undefined
              }
              onChange={(val: string | null) => {
                setFieldValue('composerIsOwner', val);
              }}
              onBlur={handleBlur}
              error={touched.composerIsOwner ? errors.composerIsOwner : undefined}
              helperText={touched?.composerIsOwner ? errors.composerIsOwner : undefined}
              required
              sort
            />
          </div>
          <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-2 md:gap-4">
            {values.composerIsOwner === 'no' && (
              <>
                <div>
                  <SelectMenu
                    label="What is the nature of your interest in the vehicle?"
                    name="natureOfInterestInVehicle"
                    listSelectedValue={values.natureOfInterestInVehicle}
                    onChange={(val: any) => {
                      setFieldValue('natureOfInterestInVehicle', val);
                    }}
                    onBlur={handleBlur}
                    list={getNatureOfInterestListByCountry(countryIso)}
                    placeholder=""
                    error={
                      touched?.natureOfInterestInVehicle
                        ? errors.natureOfInterestInVehicle
                        : undefined
                    }
                    helperText={
                      touched?.natureOfInterestInVehicle
                        ? errors.natureOfInterestInVehicle
                        : undefined
                    }
                  />
                </div>
                <Input
                  type="text"
                  id="ownerName"
                  label="Vehicle Co-owner's Name"
                  placeholder="Eg. Kwaku Manu"
                  className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                  value={values?.ownerName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.ownerName ? errors.ownerName : undefined}
                  helperText={touched?.ownerName ? errors.ownerName : undefined}
                />
                <Input
                  type="text"
                  id="ownerAddress"
                  label="Co-owner's Address"
                  placeholder="Eg. Old barrier, Kasoa"
                  className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                  value={values?.ownerAddress}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.ownerAddress ? errors.ownerAddress : undefined}
                  helperText={touched?.ownerAddress ? errors.ownerAddress : undefined}
                />
                <Input
                  type="text"
                  id="ownerPhone"
                  label="Co-owner's Phone"
                  placeholder="Eg. 0244000000"
                  className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                  value={values?.ownerPhone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.ownerPhone ? errors.ownerPhone : undefined}
                  helperText={touched?.ownerPhone ? errors.ownerPhone : undefined}
                />
              </>
            )}
          </div>
        </div>
      )
    );
  };

  const getRefereeInfoEditForm = (
    values: VehicleFormValues,
    handleBlur: any,
    handleChange: any,
    touched: FormikTouched<VehicleFormValues>,
    errors: FormikErrors<VehicleFormValues>,
    setFieldValue: any
  ) => {
    return (
      isRefereeInfoEdit && (
        <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-2 md:gap-4 p-6 rounded-lg">
          <Input
            type="text"
            id="name"
            label="Referee Name"
            placeholder="Eg. Francis Atuahene"
            className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
            value={values?.name}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <Input
            type="text"
            id="phone"
            label="Referee Phone Number"
            className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
            value={values?.phone}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <Input
            type="text"
            id="relationship"
            label="Type Of Relationship"
            className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
            value={values?.relationship}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <Input
            type="text"
            id="yearsKnown"
            label="Year Known"
            className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
            value={values?.yearsKnown}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
      )
    );
  };

  return (
    <motion.div
      className="bg-white rounded-lg shadow-sm p-6 dark:bg-darkBg"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}>
      <Formik
        initialValues={defaultValues}
        enableReinitialize
        validationSchema={
          isVehicleOwnerEdit
            ? ownerValidationSchema
            : isOtherInfoEdit
              ? undefined
              : isRefereeInfoEdit
                ? refereeValidationSchema
                : isInsuranceInfoEdit
                  ? insuranceValidationSchema
                  : vehicleValidationSchema
        }
        onSubmit={(values) => {

          const payload = {
            ...values
          };
          // onSubmit(payload)

          endorseApprovedPolicyInfo(String(policy?.ref), payload);
        }}>
        {({ errors, touched, handleChange, handleBlur, values, setFieldValue }) => {
          return (
            <Form className="space-y-4">
              {/* Vehicle Specifications Section */}
              {getVehicleInfoEditForm(
                values,
                handleBlur,
                handleChange,
                touched,
                errors,
                setFieldValue
              )}

              {/* Insurance Specifications Section */}
              {getInsuranceInfoEditForm(values, handleChange, touched, errors, setFieldValue)}

              {/* Other Info Specifications Section */}
              {getOtherInfoEditForm(values, handleChange, touched, errors)}

              {/* Vehicle Owner Section */}
              {getOwnerInfoEditForm(
                values,
                handleBlur,
                handleChange,
                touched,
                errors,
                setFieldValue
              )}

              {/* Referre Specifications Section */}
              {getRefereeInfoEditForm(
                values,
                handleBlur,
                handleChange,
                touched,
                errors,
                setFieldValue
              )}

              <div className="flex justify-between gap-4 pt-4">
                <Button
                  type="button"
                  label="Cancel"
                  bgColor="secondary"
                  fullWidth
                  onClick={onClose}
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  label={'Update'}
                  bgColor="primary"
                  fullWidth
                  loading={isLoading}
                  disabled={isLoading}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </motion.div>
  );
}
